import gsap from 'gsap';
import { CustomEase } from "gsap/CustomEase"; 
import { gsapCrescendo } from './effects';

// Loader. Text and CTA on the first scene.
export function sequence1() {
    return gsap.timeline({
        defaults: {
            duration: .3,
            ease: 'power1.in',
            opacity: 1,
            top: 0,
        },
    })
        // Animate loader out — separate display:none into onComplete so
        // the top/opacity transition actually plays before hiding
        .to('#scene-loading', {
            delay: .5,
            duration: .5,
            ease: 'power1.in',
            top: '-100%',
            opacity: 0,
            onComplete: () => {
                const el = document.getElementById('scene-loading');
                el.style.display = 'none';
                el.style.zIndex = '-1000';
            },
        })
        // Fade in scene1 pre elements
        .to('#scene1preImage', {
            delay: .3,
        })
        .to('#scene1preText', {}, '<.2')
        .to('#scene1prePlay', {}, '<.2')
        .to('#scene1preFooter', {}, '<.2')
        .to('#scene1prePlay', { onComplete: gsapCrescendo }, '<.05')
        .pause();
}

// Outside of the store. After user clicks the button,
// doors open and user is taken inside the store.
export function sequence2() {
    return gsap.timeline({
        defaults: {
            duration: 1,
            ease: 'power1.in',
        },
    })
        .to('#scene1pre', {
            duration: .3,
            ease: 'linear',
            opacity: 0,
            display: 'none',
        })
        .to('#scene1leftDoor', {
            left: '-=70',
        })
        .to('#scene1rightDoor', {
            left: '+=70',
        }, '<')
        .to('#scene1leftDoor', {
            duration: 0,
            opacity: 0,
            display: 'none',
        })
        .to('#scene1rightDoor', {
            duration: 0,
            opacity: 0,
            display: 'none',
        }, '<')
        .to('#scene1storefront', {
            ease: 'power1.inOut',
            duration: 2,
            scale: 16,
        })
        .to('#scene2', {
            duration: 1.5,
            ease: 'power1.inOut',
            scale: 1,
            onComplete: function () {
                this._targets[0].classList.add('scene--transition-complete'); 
            },
        }, '<')
        .to('#scene1storefront', {
            duration: 0,
            opacity: 0,
            display: 'none',
        })
        .to('#scene1', {
            duration: .1,
            opacity: 0,
            display: 'none',
        })
        .to('#scene2pre', {
            duration: 0,
            display: 'block',
            translateZ: 100,
        })
        .to('#scene2preImage', {
            duration: .3,
            ease: 'power1.in',
            opacity: 1,
            top: 0,
        })
        .to('#scene2preText', {
            duration: .3,
            ease: 'power1.in',
            opacity: 1,
            top: 0,
        }, '<.2')
        .to('#scene2prePlay', {
            duration: .3,
            ease: 'power1.in',
            opacity: 1,
            top: 0,
        }, '<.2')
        .to('#scene2prePlay', { onComplete: gsapCrescendo }, '<.05')
        .pause();
}

// Inside the store. After user clicks the button,
// walls fall down and mass video is revealed.
export function sequence3() {
    return gsap.timeline()
        .to('#scene2pre', {
            duration: .3,
            ease: 'linear',
            opacity: 0,
            display: 'none',
        })
        .to('#scene2logo', {
            ease: 'power3.in',
            duration: .5,
            scaleY: 1,
            skewX: 0,
            top: 0,
        })
        .to('#scene2leftWall', {
            delay: .1,
            duration: 1,
            ease: 'expo.in',
            scaleY: .1,
            skewX: '70deg',
        })
        .to('#scene2rightWall', {
            duration: 1,
            ease: 'expo.in',
            scaleY: .2,
            skewX: '-70deg',
        }, '<.3')
        .to('#scene2innerWrapper', {
            duration: .5,
            ease: 'expo.in',
            filter: 'blur(10px)',
        }, '+=.2')
        .to('#scene2innerWrapper', {
            duration: .7,
            ease: 'power2',
            scale: 2,
            top: '110%',
        }, '<')
        .to('#scene3video', {
            duration: .7,
            ease: 'power3',
            scale: 1.4,
        }, '<')
        .to('#scene2logo', {
            ease: 'linear',
            duration: .3,
            scale: 1.4,
            translateY: '-30%',
            left: '52%',
        }, '<')
        .to('#scene2logo', {
            filter: 'brightness(.5) contrast(2)',
            onComplete: function () {
                const finalStyle = this._targets[0].getAttribute('style');
                document.getElementById('scene2logoOnScene3').setAttribute('style', finalStyle);
            },
        }, '<')
        .to('#scene2logoOnScene3', {
            duration: 0,
            opacity: 1,
        })
        .to('#scene2', {
            display: 'none',
            duration: 0,
            opacity: 0,
        })
        .to('#scene3', {
            duration: 0,
            zIndex: 0,
        })
        .to('#scene2logo', {
            duration: 0,
            display: 'none',
        })
        .to('#scene3preHeader', {
            duration: .3,
            ease: 'power1.in',
            opacity: 1,
            top: 0,
        }, '<.2')
        .to('#scene3preText', {
            duration: .3,
            ease: 'power1.in',
            opacity: 1,
            top: 0,
        }, '<.2')
        .to('#scene3prePlay', {
            duration: .3,
            ease: 'power1.in',
            opacity: 1,
            top: 0,
        }, '<.2')
        .to('#scene3prePlay', { onComplete: gsapCrescendo }, '<.05')
        .pause();
}

// Mass video zooms in and is replaced with a close-up video.
export function sequence4() {
    return gsap.timeline()
        .to('#scene3pre', {
            display: 'none',
            duration: .3,
            ease: 'linear',
            opacity: 0,
        })
        .to('#scene3', {
            delay: .5,
            duration: .8,
            ease: 'expo.out',
            scale: 4,
            transformOrigin: 'center',
        })
        .to('#scene3', {
            duration: .4,
            ease: 'expo.in',
            opacity: 0,
        }, '-=1')
        .to('#scene2logo', {
            duration: .5,
            ease: 'expo.out',
            opacity: 0,
            scale: 1.5,
            translateY: '-60%',
        }, '<.2')
        .to('#scene4video', {
            duration: .1,
            ease: 'linear',
            scale: 1.1,
        }, '<.15')
        .to('#scene3', {
            display: 'none',
            duration: 0,
            opacity: 0,
        }, '<')
        .to('#scene4overlay', {
            duration: .3,
            ease: 'linear',
            opacity: 1,
            display: 'block',
        }, '<')
        .to('#scene4', {
            duration: 0,
            zIndex: 1,
        }, '<')
        .to('#scene4preHeader', {
            duration: .3,
            ease: 'power1.in',
            opacity: 1,
            top: 0,
        }, '<.4')
        .to('#scene4preText', {
            duration: .3,
            ease: 'power1.in',
            opacity: 1,
            top: 0,
        }, '<.2')
        .to('#scene4prePlay', {
            duration: .3,
            ease: 'power1.in',
            opacity: 1,
            top: 0,
        }, '<.2')
        .to('#scene4prePlay', { onComplete: gsapCrescendo }, '<.05')
        .pause();
}

export function sequence5() {
    return gsap.timeline()
        .to('#scene4pre', {
            display: 'none',
            duration: .3,
            ease: 'linear',
            opacity: 0,
        })
        .to('#scene4form1', {
            duration: 0,
            display: 'block',
        })
        .to('#scene4form1Title', {
            delay: .5,
            duration: .3,
            ease: 'power1.in',
            opacity: 1,
            top: 0,
        })
        .to('#scene4form1Text', {
            duration: .3,
            ease: 'power1.in',
            opacity: 1,
            top: 0,
        }, '<.2')
        .to('#scene4form1Form', {
            duration: .3,
            ease: 'power1.in',
            opacity: 1,
            top: 0,
        }, '<.2')
        .to('#scene4form1Privacy', {
            duration: .3,
            ease: 'power1.in',
            opacity: 1,
            top: 0,
        }, '<.2')
        .pause();
}

export function sequence6() {
    return gsap.timeline()
        .to('#scene4form1', {
            duration: .3,
            ease: 'linear',
            opacity: 0,
            display: 'none',
        })
        .to('#scene4overlay', {
            duration: .3,
            ease: 'linear',
            opacity: 0,
            display: 'none',
        }, '<')
        .to('#scenes-wrapper', {
            duration: 0,
            minWidth: 'auto',
        })
        /*.to('#scene5', {
            display: 'flex',
            duration: 0,
        })*/
        .to('#scene4', {
            delay: .5,
            duration: .8,
            ease: 'power4.in',
            height: 0,
        })
        .pause();
}
